import { useContext, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Box, Button, Section, Spinner } from '@factorialco/deprecated-design-system'
import { EnvironmentContext } from 'config/environment'
import { MarketplaceInstallationFragment } from 'generated/resources/graphql'
import { ItemIntegrationFragment } from 'generated/resources/graphql'

import InlineTooltip from 'modules/core/components/InlineTooltip'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import asyncAction from 'modules/core/lib/asyncAction'
import i18n from 'modules/core/lib/i18n'

import DynamicFieldForm from 'modules/marketplace/components/IntegrationSettings/InternalIntegrations/DynamicFieldForm'
import { useUpdateInstallation } from 'modules/marketplace/hooks/integrations/companyInstallations/useUpdateInstallation'
import { Settings } from 'modules/marketplace/hooks/integrations/formQuestions/useGetMarketplaceSettings'
import { useGetMarketplaceInstallations } from 'modules/marketplace/hooks/integrations/useGetMarketplaceInstallations'
import { useCompanyInstallations } from 'modules/marketplace/store/collections/companyInstallations'

type FormDataValue = string | string[] | number | boolean
interface FormData {
  [key: string]: FormDataValue
}

interface GenericFormProps {
  integration: ItemIntegrationFragment
  settings: Settings
}

export const GenericForm = ({
  integration: { id, datoId, authType },
  settings,
}: GenericFormProps) => {
  const session = useCurrentSession()
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const environment = useContext(EnvironmentContext)
  const oauth_redirect = `${environment.apiEndpoint}/integrations/${datoId}/authorize`
  const { updateInstallation } = useUpdateInstallation()

  const companyInstallations = useCompanyInstallations()
  const [installation, setInstallation] = useState<MarketplaceInstallationFragment | undefined>()
  const [formData, setFormData] = useState<FormData>({})

  const {
    data: installationsData,
    loading,
    refetch: refetchInstallationData,
  } = useGetMarketplaceInstallations({
    // FIXME: We should render the component only when there is an id
    integrationIds: [id ?? '0'],
  })
  const installationData = installationsData.at(0)

  useUnsafeEffect(() => {
    installationData && setInstallation(installationData)
  }, [installationData])

  useUnsafeEffect(() => {
    installation && installationData && setFormData(JSON.parse(installationData?.formData ?? '{}'))
  }, [installationData, installation])

  const handleFormChange = (fieldName: string, value: FormDataValue) => {
    setFormData((prevState: FormData) => ({ ...prevState, [fieldName]: value }))
  }

  const handleOnClick = () => {
    installation ? handleInstallationEdition(formData) : handleInstallation(formData)
  }

  const handleInstallation = async (data: FormData) => {
    const success = await createInstallation(data)
    if (success) {
      refetchInstallationData()
      track(trackingEvents.marketplace.integrationInstalledSuccesfully, {
        integration_id: id,
        company_id: session.company.id,
      })
      authType === 'oauth'
        ? window.location.replace(oauth_redirect)
        : navigate(navigation.discover.marketplace.item.fullPath({ id: datoId }))
    }
  }

  const handleInstallationEdition = async (data: FormData) => {
    if (!installation) return
    const success = await updateInstallation(installation, data)
    if (success) {
      refetchInstallationData()
      navigate(navigation.discover.marketplace.item.fullPath({ id: datoId }))
      track(trackingEvents.marketplace.integrationUpdatedSuccesfully, {
        integration_id: id,
        company_id: session.company.id,
      })
    }
  }

  const createInstallation = async (data: FormData) => {
    const payload = {
      access_id: session.access.id,
      form_data: data,
      company_id: session.company.id,
      integration_id: id,
    }

    const { success } = await asyncAction(
      async () => {
        await companyInstallations.create(payload)
      },
      { loadingMessage: i18n.t('common.loading'), successFlash: true }
    )
    return success
  }

  const buttonDisabled = () => {
    const requiredQuestionsKeys = settings.questions
      .map((question) => question.required && question.id)
      .filter(Boolean)

    const areRequiredQuestionsNotFilled = (questionKey: string) => !formData[questionKey]
    return requiredQuestionsKeys.some(areRequiredQuestionsNotFilled)
  }

  return (
    <Section
      title={settings.title}
      description={settings.description || ''}
      illustration="settings"
    >
      <Box padding="s32" borderRadius={{ all: 'abs012' }} background="grey200" gap="s24">
        {loading || (installationData && Object.keys(formData).length === 0) ? (
          <Spinner />
        ) : (
          <>
            {settings.questions.map((question, index) => {
              return (
                <DynamicFieldForm
                  key={index}
                  field={question}
                  value={formData[question.id] || ''}
                  onChange={(value) => handleFormChange(question.id, value)}
                />
              )
            })}
            {settings.hasEncryptedFields && (
              <Box gap="s8">
                <InlineTooltip arrow="top" type="accent">
                  {i18n.t('personal_settings.encrypted_fields_info')}
                </InlineTooltip>
              </Box>
            )}
            <Box alignSelf="flexEnd">
              <Button onClick={handleOnClick} disabled={buttonDisabled()}>
                {i18n.t('common.submit')}
              </Button>
            </Box>
          </>
        )}
      </Box>
    </Section>
  )
}
